import React, { Fragment, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useHistory } from "react-router-dom";
import { extent, merge } from 'd3-array';
import { scaleTime } from 'd3-scale';
import { timeDay, timeHour } from 'd3-time';

import ActivityOverlay from './ActivityOverlay';
import BaseSVG, { zeroMargins } from './BaseSVG';
import { getActivityAccessor, getActivityTextAccessor } from './DayInfoGraph';
import D3Component from './D3Component';
import GlucoseSVG from './GlucoseSVG';
import GlucoseDayGraph from './GlucoseDayGraph';
import MainView from './MainView';
import PresentationContext from '../PresentationContext';
import TreatmentsOverlay from './TreatmentsOverlay';
import { timeSort, timeAccessor } from '../utils/dataUtils';
import { parseDate, urlDateFormat } from '../utils/formats';
import './Dashboard.css';


const extendedWidth = 720 / 24 * 32;

function getTimeScaleForDay({ day }) {
  const date = timeDay(parseDate(day));
  const domain = [date, timeHour.offset(date, 32)]; // until 8 am the next morning
  const scale = scaleTime()
    .domain(domain)
    .range([0, extendedWidth]);
  return function timeScale(d) {
    return scale(timeAccessor(d));
  }
};

function parseActivityFromUrl() {
  const url = new URL(document.location.href);
  return url.searchParams.get('activity');
}

function ExerciseReport(props) {
  const [selectedActivity, setSelectedActivity] = useState(parseActivityFromUrl());
  const [selectedExerciseIndexes, setSelectedExerciseIndexes] = useState({});
  const [visibleDayCount, setVisibleDayCount] = useState(20);
  const scrollInspectorElement = useRef(null);

  const defaultHistory = useHistory();
  const { /* dayTimeFormat, */ formatDateRange, formatDateString, formatGlucose, formatTimeString } = useContext(PresentationContext);

  const {
    basalData,
    bolusData,
    exerciseData,
    history = defaultHistory,
    interactive = true,
    // notesData,
    nutritionData,
    sgvData,
    stepsData = {},
    t,
  } = props;

  // const noteAccessor = d => `${dayTimeFormat(new Date(d.time))}: ${d.note}`;

  const activityTypeAccessor = useMemo(() => getActivityAccessor(t), [t]);
  const activityTextAccessor = useMemo(() => getActivityTextAccessor(t), [t]);

  function timeAndTypeSort(a,b) {
    const order = timeSort(a,b);
    if (order === 0) {
      // same time, order by type instead.
      return activityTextAccessor(a) < activityTextAccessor(b) ? -1 : 1;
    }
    return order;
  }
  
  const exerciseDays = Object.keys(exerciseData).sort();
  const walkingDays = Object.keys(stepsData).sort();
  let firstDay = walkingDays[0];
  if (exerciseDays[0] && !(firstDay > exerciseDays[0])) {
    firstDay = exerciseDays[0];
  }
  let lastDay = walkingDays[walkingDays.length - 1];
  if (exerciseDays[0] && !(lastDay < exerciseDays[exerciseDays.length - 1])) {
    lastDay = exerciseDays[exerciseDays.length - 1];
  }

  function selectActivity(activity) {
    setSelectedActivity(activity);
    const target = {};
    if (activity) {
      target.search = `?${new URLSearchParams({ activity }).toString()}`;
    }
    history.push(target);
    const firstOfType = exerciseDays.reduce((o, d) => {
      // IE does not support .findIndex...
      const exercisesOfDay = exerciseData[d] || [];
      for (let i = 0; i < exercisesOfDay.length; i += 1) {
        if (activityTypeAccessor(exercisesOfDay[i]) === activity) {
          o[d] = i;
          break;
        }
      }
      return o;
    }, {});
    setSelectedExerciseIndexes(firstOfType);
  }

  const scrollObserver = window.IntersectionObserver
    ? new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting) {
        scrollObserver.disconnect();
        setVisibleDayCount(visibleDayCount + 20);
      }
    })
    : {
      // Just visualize all exercises at once
      // We could implement an onScroll based listener for IE, or use a polyfill...
      // See https://github.com/w3c/IntersectionObserver
      observe: () => setVisibleDayCount(undefined),
      disconnect: f => f,
    }


  useEffect(() => {
    if (scrollInspectorElement.current) {
      scrollObserver.observe(scrollInspectorElement.current);
    }
    return () => scrollObserver.disconnect();
  });

  /*
  // tag the latest entry of each hypo episode
  const hypos = merge(Object.values(sgvData)).reduce((o, v, i, a) => {
    const glucose = glucoseAccessor(v);
    const hypoLimit = consts[internalUnits].ticks.hypo;
    if (glucose > hypoLimit) {
      return o;
    }
    if ((i < (a.length - 1)) && (glucoseAccessor(a[i + 1]) > hypoLimit)) {
      return o;
    }
    o.push(v);
    return o;
  }, []).sort(timeSort);
  */

  const days = timeDay.range(parseDate(firstDay), timeDay.offset(parseDate(lastDay), 1)).map(urlDateFormat);

  const types = Object.values(exerciseData).reduce((o, dailyExercises) => {
    dailyExercises.forEach((e) => {
      if (!e.duration) {
        // We have erroneous entries, where an activity was started by accident, then immediately cancelled.
        return o;
      }
      // const type = e.type || e.exercise || e.activity || 'undefined activity';
      const type = activityTypeAccessor(e);
      if (!o[type]) {
        o[type] = 0;
      }
      o[type] += 1;
    });
    return o;
  }, {});

  const options = Object.keys(types).map(type => ({
    value: type,
    label: `${t(type, { ns: 'activities' })} (${types[type]})`,
  })).sort((a,b) => a.label < b.label ? -1 : 1);

  const activityDays = useMemo(() => {
    if (!selectedActivity) {
      return exerciseData;
    }
    const aDays = Object.keys(exerciseData)
      .reduce((o, d) => {
        if (exerciseData[d].some(a => ((activityTypeAccessor(a) === selectedActivity) && a.duration))) {
          o[d] = exerciseData[d].filter(a => !!a.duration);
        }
        return o;
      }, {});
    /*
    if (selectedActivity === 'walking') {
      Object.keys(stepsData).forEach((d) => {
        if (aDays[d]) {
          aDays[d] = [...aDays[d], ...stepsData[d]].sort(timeSort);
        } else {
          aDays[d] = stepsData[d];
        }
      });
    }
    */
    return aDays;
  }, [exerciseData, stepsData, selectedActivity]);

  const basalExtent = useMemo(() => extent(merge(Object.values(basalData)), n => n.rate), [basalData]);
  const bolusExtent = useMemo(() => extent(merge(Object.values(bolusData)), n => n.amount), [bolusData]);
  const carbExtent = useMemo(() => extent(merge(Object.values(nutritionData)), n => n.carbohydrate), [nutritionData]);

  // console.log({ basalData, bolusData, nutritionData });
  
  const setSelectedExerciseIndexForDay = useMemo(() => (day, index) => {
    setSelectedExerciseIndexes({
      ...selectedExerciseIndexes,
      [day]: index,
    });
  }, [selectedExerciseIndexes, setSelectedExerciseIndexes]);

  return (
    <MainView
      heading={t('exercise.heading')}
      interactive={interactive}
      key="mainView"
      title={`Sensotrend Exercise Report ${formatDateRange(days)}`}
    >
      <section id="exercises">
        <select
          id="activitySelect"
          defaultValue={selectedActivity}
          onChange={(event) => {
            selectActivity(event.target.selectedOptions[0].value || undefined);
          }}
        >
          <option value="">{`${t('activity', { ns: 'activities' })}:`}</option>
          {options.map(o => (
            <option
              key={o.value}
              value={o.value}
            >
              {o.label}
            </option>
          ))}
        </select>

        <ul className="activityList">
          {Object.keys(activityDays).sort().reverse().slice(0, visibleDayCount).map((day, i) => {
            const date = parseDate(day);
            const nextDate = timeDay.offset(date, 1);
            const previousDay = urlDateFormat(timeDay.offset(date, -1));
            const nextDay = urlDateFormat(nextDate);
            const treatmentsOverlayData = {
              basal: [...(basalData[previousDay] || []), ...(basalData[day] || [])],
              bolus: [...(bolusData[previousDay] || []), ...(bolusData[day] || [])],
              glucose: [],
              nutrition: [...(nutritionData[previousDay] || []), ...(nutritionData[day] || [])],
            }

            const activityOverlayData = {
              activity: (activityDays[day] || []).sort(timeAndTypeSort),
              basal: [],
              bolus: [],
              glucose: [],
              nutrition: [],
              selectedExerciseIndex: selectedExerciseIndexes[day],
              setSelectedExerciseIndex: (index) => {
                setSelectedExerciseIndexForDay(day, index);
              },
            }
            /*
            const uniqueNotes = Object.values(
              (notesData[day] || [])
                .filter(
                  n => n.note
                    && (n.note.indexOf('AndroidAPS') !== 0)
                    && (n.note.indexOf('Pump unreachable') !== 0)
                    && (n.note.indexOf('The pump history has changed') !== 0)
                    && (n.note.indexOf('Not enough insulin for bolus left in reservoir') !== 0)
                    && (n.note.indexOf('The pump could not be reached. No bolus was given') !== 0)
                )
                .reduce((o, n) => {
                  o[n.id] = n;
                  return o;
                }, {})).sort(timeSort);
            */

            const timeScale = getTimeScaleForDay({ day });

            const times = [ parseDate(day), timeHour.offset(parseDate(nextDay), 8) ];

            const underlay = (
              <Fragment>
                <D3Component
                  {...activityOverlayData}
                  basalExtent={basalExtent}
                  bolusExtent={bolusExtent}
                  carbExtent={carbExtent}
                  className="exercise"
                  day={day}
                  formatTimeString={formatTimeString}
                  graph={ActivityOverlay}
                  t={t}
                  timeScale={timeScale}
                />
                <D3Component
                  {...treatmentsOverlayData}
                  basalExtent={basalExtent}
                  bolusExtent={bolusExtent}
                  carbExtent={carbExtent}
                  className="treatments"
                  day={day}
                  formatTimeString={formatTimeString}
                  graph={TreatmentsOverlay}
                  t={t}
                  timeScale={timeScale}
                />
              </Fragment>
            );

            const svgProps = {
              margins: { top: 10, right: 1, bottom: 110, left: 100 },
              offsets: { top: 0, left: 0 },
              svgHeight: '100%',
              svgWidth: '100%',
              /*
              svgWidth: `${30 / 720 * extendedWidth}%`,
              */
              style: { paddingBottom: '2em' },
              times,
              underlay,
              width: extendedWidth,
            }

            return (
              <li key={day} ref={i === visibleDayCount - 5 ? scrollInspectorElement : undefined}>
                  <h3 className="activity ">
                    <Link to={`../day/${day}`}>{formatDateString(day)}</Link>
                    <span className="weekDay">
                      {t(`dashboard:common.days.${parseDate(day).getDay()}`)}
                    </span>
                  </h3>
                  <GlucoseSVG {...svgProps}>
                    <D3Component
                      data={
                        { sgv: [...(sgvData[day] || []), ...(sgvData[nextDay] || [])] }
                      }
                      day={day}
                      days={[day]}
                      formatGlucose={formatGlucose}
                      graph={GlucoseDayGraph}
                      timeScale={timeScale}
                    />
                  </GlucoseSVG>
              </li>
            );
          })}
        </ul>
      </section>
      <section>
        <BaseSVG height={0} svgWidth={0} svgHeight={0} margins={zeroMargins} showXAxis={false} key="firstAdditionalGraph" />
      </section>
      <section>
        <BaseSVG height={0} svgWidth={0} svgHeight={0} margins={zeroMargins} showXAxis={false} key="secondAdditionalGraph" />
      </section>
    </MainView>
  );
}

export default ExerciseReport;
